import React, { useState } from 'react';
import Blog from '../blogs/Template/Blog';
import { Typography, Box, Divider, TextField, MenuItem, FormControlLabel, Checkbox, Button, Paper, Grid } from '@mui/material';

const CalculatorWidget = () => {
    const [price, setPrice] = useState(25.00);
    const [cost, setCost] = useState(10.00);
    const [weight, setWeight] = useState(0.82);
    const [length, setLength] = useState(10.3);
    const [width, setWidth] = useState(7.5);
    const [height, setHeight] = useState(2.4);
    const [category, setCategory] = useState(0.15);
    const [isApparel, setIsApparel] = useState(false);
    const [results, setResults] = useState(null);

    const calculateProfit = () => {
        let catFee = parseFloat(category);
        let referralFee = price * catFee;
        
        let dimWeight = (length * width * height) / 139;
        let baseWeight = Math.max(weight, dimWeight);
        let billableWeight = Math.ceil(baseWeight + 0.25);
        
        let wfsFee = 0;
        if (billableWeight <= 1) wfsFee = 3.45;
        else if (billableWeight === 2) wfsFee = 4.95;
        else if (billableWeight === 3) wfsFee = 5.45;
        else if (billableWeight <= 20) wfsFee = 5.75 + ((billableWeight - 4) * 0.40);
        else if (billableWeight <= 30) wfsFee = 15.55 + ((billableWeight - 21) * 0.40);
        else wfsFee = 15.55; 
        
        if (isApparel) wfsFee += 0.50;
        if (price > 0 && price < 10) wfsFee += 1.00;

        let totalFees = referralFee + wfsFee;
        let profit = price - cost - totalFees;
        let margin = price > 0 ? (profit / price) * 100 : 0;

        setResults({ billableWeight, referralFee, wfsFee, totalFees, profit, margin });
    };

    return (
        <Paper elevation={3} sx={{ p: 4, my: 4, borderRadius: 2, backgroundColor: '#f9f9f9' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold" color="#ffca18">
                Marter Profit Calculator
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Selling Price ($)" type="number" value={price} onChange={(e) => setPrice(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Cost of Goods ($)" type="number" value={cost} onChange={(e) => setCost(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={12}>
                    <TextField fullWidth label="Actual Weight (lbs)" type="number" value={weight} onChange={(e) => setWeight(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={4}>
                    <TextField fullWidth label="Length (in)" type="number" value={length} onChange={(e) => setLength(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={4}>
                    <TextField fullWidth label="Width (in)" type="number" value={width} onChange={(e) => setWidth(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={4}>
                    <TextField fullWidth label="Height (in)" type="number" value={height} onChange={(e) => setHeight(parseFloat(e.target.value) || 0)} />
                </Grid>
                <Grid item xs={12}>
                    <TextField select fullWidth label="Product Category (Referral Fee)" value={category} onChange={(e) => setCategory(e.target.value)}>
                        <MenuItem value={0.15}>Standard (15% - Home, Toys, etc.)</MenuItem>
                        <MenuItem value={0.12}>Industrial / Automotive (12%)</MenuItem>
                        <MenuItem value={0.08}>Electronics / Major Appliances (8%)</MenuItem>
                    </TextField>
                </Grid>
                <Grid item xs={12}>
                    <FormControlLabel 
                        control={<Checkbox checked={isApparel} onChange={(e) => setIsApparel(e.target.checked)} />} 
                        label="This is an Apparel Item (+$0.50 WFS Fee)" 
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" fullWidth size="large" onClick={calculateProfit} sx={{ py: 1.5, fontWeight: 'bold', backgroundColor: '#ffca18', color: '#000' }}>
                        Calculate Profit
                    </Button>
                </Grid>
            </Grid>

            {results && (
                <Box sx={{ mt: 4, p: 3, backgroundColor: '#ffffff', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                    <Typography variant="body1"><strong>Billable Weight:</strong> {results.billableWeight} lbs</Typography>
                    <Typography variant="body1"><strong>Referral Fee:</strong> ${results.referralFee.toFixed(2)}</Typography>
                    <Typography variant="body1"><strong>WFS Fee:</strong> ${results.wfsFee.toFixed(2)}</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}><strong>Total Fees:</strong> ${results.totalFees.toFixed(2)}</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="h6" sx={{ color: results.profit >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                        Net Profit: ${results.profit.toFixed(2)}
                    </Typography>
                    <Typography variant="body1"><strong>Profit Margin:</strong> {results.margin.toFixed(1)}%</Typography>
                </Box>
            )}
        </Paper>
    );
};

const ProfitCalculator = () => {
    const blogData = {
        title: "Walmart Profit Calculator — Free WFS & Referral Fee Calculator",
        description: "Calculate profitability and margins on any product on Walmart Marketplace with Marter's Profit Calculator. Input your product details and instantly see your net profit, fees, and margins to make informed selling decisions on Walmart WFS.",
        keywords: ["walmart profit calculator", "walmart fee calculator", "walmart seller calculator", "wfs fee calculator", "walmart referral fee calculator", "calculate profit on walmart", "free walmart profit calculator"],
        canonical: "https://www.getmarter.com/tools/profit-calculator",
        alt: "Walmart Profit Calculator",
        author: "Ido Sarusi",
        content: (
            <Box sx={{ mb: 6 }}>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                   Calculate profitability and margins on any product on Walmart Marketplace with <strong>Marter's Profit Calculator</strong>. Input your product details and instantly see your net profit, fees, and margins to make informed selling decisions on Walmart WFS.
                </Typography>
                
                <CalculatorWidget />
            </Box>
        ),
        recommendedPosts: [
            { title: 'How to find profitable products', url: '/blogs/how-to-find-profitable-products' },
            { title: 'How to Use Product History to Choose Winning Products', url: '/blogs/historical-data' },
            { title: 'How to optimize your Walmart listings', url: '/blogs/how-to-optimize-your-walmart-listings' },
            { title: 'How to rank higher on Walmart', url: '/blogs/how-to-rank-higher-on-walmart' }
        ],
        // description/keywords/ogImage here were copy-pasted from the Amazon FBA vs
        // Walmart WFS post and described that page instead of this one.
        meta: {
            description: "Free Walmart profit calculator: enter price, cost, weight and dimensions to get referral fees, WFS fulfillment fees, net profit and margin for any Walmart Marketplace item. No signup.",
            keywords: "walmart profit calculator, walmart fee calculator, walmart seller calculator, wfs fee calculator, walmart referral fee calculator, free walmart profit calculator, calculate profit on walmart",
            ogImage: "/showcase.png",
            canonical: "https://www.getmarter.com/tools/profit-calculator",
            dateModified: "2026-08-09",
            socialSharing: {
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`,
                twitter: `https://twitter.com/intent/tweet?url=${window.location.href}`,
                linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`,
                whatsapp: `https://api.whatsapp.com/send?text=${window.location.href}`,
                telegram: `https://t.me/share/url?url=${window.location.href}`,
                reddit: `https://www.reddit.com/submit?url=${window.location.href}&title=${document.title}`
            }
        }
    };

    return <Blog {...blogData} />;
};

export default ProfitCalculator;
